import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Phone, ShowerHead, Wifi, Tv, Briefcase, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { Reveal, Container, ParallaxImg } from "@/components/common/Primitives";
import { PageHero, SectionHead, Rows, Kicker, Headline, Lead, TextLink, RuleDraw, ClosingBand, HGallery, Index } from "@/components/common/Editorial";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { PHONE } from "@/components/layout/Header";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

/* Facilities common to every hotel room (used by RoomDetail) */
export const FACILITIES = [
  { icon: ShowerHead, nn: "Bad med dusj", en: "Bathroom with shower" },
  { icon: Wifi, nn: "Trådlaust nett", en: "Wireless internet" },
  { icon: Tv, nn: "TV og telefon", en: "TV and phone" },
  { icon: Briefcase, nn: "Arbeidspult", en: "Desk" },
  { icon: Wind, nn: "Hårtørkar og vasskokar", en: "Hairdryer and kettle" },
];

/* copy (binding – memory/copy_deck_nynorsk.md §3) – shared with RoomDetail */
export const ROOM_COPY = {
  "standard-fjord": {
    nn: ["Balkong med utemøblar og utsikt over Hardangerfjorden. Rommet har senger som står saman og eige bad med dusj og toalett.", ["Fjordutsikt", "Balkong", "Bad med dusj", "Trådlaust nett", "Frukost inkludert"]],
    en: ["A balcony with outdoor furniture and a view over the Hardangerfjord. The room has beds placed together and a private bathroom with shower and toilet.", ["Fjord view", "Balcony", "Bathroom with shower", "Wireless internet", "Breakfast included"]],
  },
  "standard-mountain": {
    nn: ["Utsikt mot Rosendalsfjella, senger som står saman og eige bad med dusj og toalett. Romma finst med parkett eller teppe.", ["Fjellutsikt", "Bad med dusj", "Trådlaust nett", "Frukost inkludert"]],
    en: ["A view of the Rosendal mountains, beds placed together and a private bathroom with shower and toilet. The rooms come with parquet or carpet.", ["Mountain view", "Bathroom with shower", "Wireless internet", "Breakfast included"]],
  },
  juniorsuite: {
    nn: ["Fjordutsikt, balkong og ei eiga sitjegruppe. Juniorsuiten gir deg plass til å trekkje deg tilbake og bli verande på rommet ei stund.", ["Fjordutsikt", "Balkong", "Dobbeltseng", "Sitjegruppe", "Vasskokar", "Minikjøleskap", "Frukost inkludert"]],
    en: ["A fjord view, a balcony and your own seating area. The junior suite gives you room to withdraw and stay in for a while.", ["Fjord view", "Balcony", "Double bed", "Seating area", "Kettle", "Mini fridge", "Breakfast included"]],
  },
  brudesuite: {
    nn: ["Ein romsleg suite med dobbeltseng, sitjegruppe og stor balkong. Dei to brudesuitane har ulik utsikt: mot fjorden eller Rosendalsfjella.", ["Fjord- eller fjellutsikt, avhengig av suite", "Stor balkong", "Dobbeltseng", "Sitjegruppe", "Vasskokar", "Minikjøleskap", "Frukost inkludert"]],
    en: ["A spacious suite with a double bed, seating area and a large balcony. The two bridal suites have different views: the fjord or the Rosendal mountains.", ["Fjord or mountain view, depending on the suite", "Large balcony", "Double bed", "Seating area", "Kettle", "Mini fridge", "Breakfast included"]],
  },
  superiorsuite: {
    nn: ["Suite med utsikt mot Rosendalsfjella, dobbeltseng, sofa og eige matbord. Her er det god plass til å sitje saman.", ["Fjellutsikt", "Dobbeltseng", "Sofa og matbord", "Vasskokar", "Minikjøleskap", "Frukost inkludert"]],
    en: ["A suite with a view of the Rosendal mountains, a double bed, a sofa and its own dining table. Plenty of room to sit together.", ["Mountain view", "Double bed", "Sofa and dining table", "Kettle", "Mini fridge", "Breakfast included"]],
  },
  sidefloy: {
    nn: ["Eit mindre rom med senger som står saman og eige bad. Kjæledyr er velkomne etter avtale. Det er ikkje plass til ekstraseng i desse romma.", ["Eigen inngang", "Parkering rett utanfor", "Bad med dusj", "Kjæledyr etter avtale", "Frukost inkludert"]],
    en: ["A smaller room with beds placed together and a private bathroom. Pets are welcome by arrangement. There is no room for an extra bed in these rooms.", ["Private entrance", "Parking right outside", "Bathroom with shower", "Pets by arrangement", "Breakfast included"]],
  },
};

const SECTIONS = {
  hovudbygget: {
    nn: ["Standardrom", "Rom med fjord- eller fjellutsikt", "Standardromma gir deg to ulike utsyn: mot Hardangerfjorden eller Rosendalsfjella. Vel fjordutsikt om du òg ønskjer balkong."],
    en: ["Standard rooms", "Rooms with a fjord or mountain view", "The standard rooms give you two different outlooks: the Hardangerfjord or the Rosendal mountains. Choose the fjord view if you also want a balcony."],
  },
  suitane: {
    nn: ["Suitar", "Litt meir plass under opphaldet", "Vel mellom fire juniorsuitar, to brudesuitar og éin superiorsuite. Alle har dobbeltseng, sitjeplass, vasskokar og minikjøleskap."],
    en: ["Suites", "A little more space during your stay", "Choose between four junior suites, two bridal suites and one superior suite. All have a double bed, seating, a kettle and a mini fridge."],
  },
  sidefloya: {
    nn: ["Eigen inngang · Kjæledyr etter avtale", "Rett inn frå uteområdet", "Tolv mindre rom med eigen inngang på bakkeplan og parkering rett utanfor. Sidefløya passar for deg som ønskjer enkel tilkomst, eller vil ha med kjæledyr."],
    en: ["Private entrance · Pets by arrangement", "Straight in from outside", "Twelve smaller rooms with their own ground-level entrance and parking right outside. The side wing suits you if you want easy access, or are bringing a pet."],
  },
};

const CABIN_ROWS = {
  nn: [["Gjester", "Inntil åtte gjester"], ["Inne", "Stove og kjøkenkrok"], ["Matplass", "Til åtte"], ["Bad", "Eige bad"], ["Ute", "Terrasse med fjordutsikt"], ["Opphald", "Minst to netter"]],
  en: [["Guests", "Up to eight guests"], ["Inside", "Living room and kitchenette"], ["Dining", "Seats eight"], ["Bathroom", "Private bathroom"], ["Outside", "Terrace with a fjord view"], ["Stay", "Minimum two nights"]],
};

const INCLUDED = {
  nn: [["Frukost", "Frukostbuffé i Restaurant Baronen"], ["Parkering", "Gratis ved hotellet"], ["Nett", "Trådlaust nett i heile hotellet"], ["Bad", "Eige bad med dusj og toalett på alle rom"], ["Røykfritt", "Alle hotellromma er røykfrie"]],
  en: [["Breakfast", "Breakfast buffet in Restaurant Baronen"], ["Parking", "Free at the hotel"], ["Internet", "Wireless internet throughout the hotel"], ["Bathroom", "Private bathroom with shower and toilet in every room"], ["Non-smoking", "All hotel rooms are non-smoking"]],
};

const RoomRow = ({ rt, index, flip, eager }) => {
  const { lang, tx, tr } = useLang();
  const copy = ROOM_COPY[rt.id] ? (lang === "en" ? ROOM_COPY[rt.id].en : ROOM_COPY[rt.id].nn) : [tr(rt.tagline), rt.highlights?.[lang] || []];
  const [body, facilities] = copy;
  const price = minPrice(rt);
  const id = `room-row-${rt.id}`;
  return (
    <article className="grid items-center gap-7 lg:grid-cols-12 lg:gap-12" data-testid={id}>
      <Link to={`/rom/${rt.slug}`} className={`group block lg:col-span-7 ${flip ? "lg:order-2" : ""} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4`} aria-label={tr(rt.name)} data-testid={`${id}-image-link`}>
        <ParallaxImg src={rt.images?.[0]} alt={tr(rt.name)} className="aspect-[4/3] lg:aspect-[16/10]" sizes={SIZES.half} eager={eager} range={4} />
      </Link>
      <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:pr-4" : "lg:pl-4"}`}>
        <Index n={index} label={tx(`Inntil ${rt.max_guests} gjester`, `Up to ${rt.max_guests} guests`)} />
        <h3 className="mt-5 text-[1.75rem] font-medium leading-[1.1] tracking-[-0.02em] text-foreground sm:text-[2rem]" data-testid={`${id}-title`}>{tr(rt.name)}</h3>
        <p className="mt-3 max-w-[46ch] text-[1.0625rem] leading-[1.6] text-muted-foreground">{body}</p>
        <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-2 border-t border-border pt-5" data-testid={`${id}-facilities`}>
          {facilities.map((f) => <li key={f} className="label-caps text-foreground/70">{f}</li>)}
        </ul>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
          <Button asChild data-testid={`${id}-link`}><Link to={`/rom/${rt.slug}`}>{tx("Sjå pris og ledige rom", "See price and availability")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
          {price && <p className="text-sm text-muted-foreground tabular" data-testid={`${id}-price`}>{tx("Frå", "From")} <span className="font-medium text-foreground">{nok(price, lang)}</span> {tx("per natt · frukost inkl.", "per night · breakfast incl.")}</p>}
        </div>
      </div>
    </article>
  );
};

export default function Rooms() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const isMobile = useIsMobile();
  const buildings = data?.buildings || [];
  const roomTypes = data?.room_types || [];
  const cabins = data?.settings?.cabins;
  usePageMeta(tx("Rom og hytter", "Rooms and cabins"), tx("Fjord- eller fjellutsikt, meir plass i ein suite eller ei hytte for fleire. Frukost er inkludert i alle hotellrom.", "A fjord or mountain view, more space in a suite or a cabin for a group. Breakfast is included in all hotel rooms."));

  const shortcuts = [
    ...buildings.map((b) => ({ id: b.id, label: tr(b.name) })),
    { id: "hytter", label: tx("Hytter", "Cabins") },
  ];

  const gallery = [
    { src: "/images/room-fjord-balcony.jpg", title: tx("Balkong mot Hardangerfjorden", "Balcony facing the Hardangerfjord"), body: tx("Standardrom med fjordutsikt", "Standard room, fjord view"), tall: true },
    { src: "/images/suite-superior.jpg", title: tx("Superiorsuite med matbord", "Superior suite with dining table"), body: tx("Suitane", "The suites") },
    { src: "/images/suite-junior-lounge.jpg", title: tx("Sitjegruppe i juniorsuiten", "Seating area in the junior suite"), body: tx("Suitane", "The suites") },
    { src: "/images/room-mountain-view.jpg", title: tx("Fjella utanfor vindauget", "The mountains outside the window"), body: tx("Standardrom med fjellutsikt", "Standard room, mountain view") },
    { src: "/images/room-sidewing.jpg", title: tx("Eigen inngang i sidefløya", "A private entrance in the side wing"), body: tx("Sidefløya", "The side wing"), position: "50% 55%" },
    { src: "/images/bathroom-junior.jpg", title: tx("Bad med dusj", "Bathroom with shower"), body: tx("Alle rom", "Every room"), tall: true },
  ];

  let n = 0;

  return (
    <div data-testid="rooms-page">
      <PageHero
        testid="rooms-hero"
        image="/images/room-fjord-window.jpg"
        position="50% 55%"
        alt={tx("Standardrom med fjordutsikt – vindauge mot fjorden og fjella", "Standard room with fjord view – window facing the fjord and mountains")}
        kicker={tx("Rom og hytter", "Rooms and cabins")}
        title={tx("Vel korleis du vil bu", "Choose how you want to stay")}
        lead={tx("Fjord- eller fjellutsikt, meir plass i ein suite eller ei hytte for fleire. Her finn du overnattinga som passar dagane dine i Rosendal. Frukost er inkludert i alle hotellrom.", "A fjord or mountain view, more space in a suite or a cabin for a group. Here you find the stay that suits your days in Rosendal. Breakfast is included in all hotel rooms.")}
        height="h-[84svh] min-h-[600px] max-h-[960px]"
      >
        {!isMobile && (
          <div className="mt-10 w-full max-w-[980px] pb-2" data-sticky-anchor>
            <BookingBar testid="rooms-booking-bar" />
          </div>
        )}
      </PageHero>

      {/* Shortcuts */}
      <Container className="pt-8 lg:pt-10">
        <nav className="no-scrollbar -mx-5 flex items-center gap-7 overflow-x-auto border-b border-border px-5 sm:mx-0 sm:px-0 lg:gap-9" aria-label={tx("Snarvegar", "Shortcuts")} data-testid="rooms-building-nav">
          {shortcuts.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="link-grow shrink-0 pb-4 text-[0.875rem] font-medium tracking-[0.01em] text-foreground/70 transition-colors hover:text-foreground" data-testid={`rooms-nav-${s.id}`}>{s.label}</a>
          ))}
        </nav>
      </Container>

      {/* Buildings */}
      {buildings.map((b, bi) => {
        const rooms = roomTypes.filter((r) => r.building === b.id);
        const sec = SECTIONS[b.id] ? (lang === "en" ? SECTIONS[b.id].en : SECTIONS[b.id].nn) : [tr(b.tagline), tr(b.name), tr(b.description)];
        return (
          <section key={b.id} id={b.id} className="scroll-mt-28 pt-24 lg:pt-32" data-testid={`rooms-building-${b.id}`}>
            <Container>
              {bi > 0 && <RuleDraw className="mb-16 lg:mb-24" />}
              <Reveal><SectionHead kicker={sec[0]} title={sec[1]} lead={sec[2]} testid={`rooms-building-${b.id}-title`} /></Reveal>
              <div className="mt-12 space-y-20 lg:mt-16 lg:space-y-28" data-testid={`rooms-building-${b.id}-list`}>
                {rooms.map((rt) => { n += 1; return <Reveal key={rt.id}><RoomRow rt={rt} index={n} flip={n % 2 === 0} eager={n === 1} /></Reveal>; })}
              </div>
              {b.id === "sidefloya" && (
                <Reveal className="mt-10 max-w-2xl border-t border-border pt-6 text-[0.9375rem] leading-relaxed text-muted-foreground lg:mt-14" data-testid="rooms-pets-note">
                  {tx("Reiser du med kjæledyr? Kontakt oss før du bestiller, så stadfestar me ledig rom og pristillegg.", "Travelling with a pet? Contact us before you book and we will confirm availability and the supplement.")}
                </Reveal>
              )}
            </Container>
          </section>
        );
      })}

      {/* Cabins */}
      <section id="hytter" className="scroll-mt-28 pt-24 lg:pt-32" data-testid="rooms-cabins">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="order-2 lg:order-1 lg:col-span-5">
              <Kicker className="mb-5">{tx("Hyttene", "The cabins")}</Kicker>
              <Headline>{tx("Ei hytte heilt ved fjorden", "A cabin right by the fjord")}</Headline>
              <Lead>{tx("Bu saman med familie eller vener, med terrasse mot fjorden og plass til inntil åtte. Hyttene har stove, kjøkenkrok med matplass og eige bad.", "Stay together with family or friends, with a terrace facing the fjord and room for up to eight. The cabins have a living room, a kitchenette with dining space and a private bathroom.")}</Lead>
              <div className="mt-8"><Rows rows={lang === "en" ? CABIN_ROWS.en : CABIN_ROWS.nn} labelWidth="sm:grid-cols-[7rem_1fr]" testid="rooms-cabin-facts" /></div>
              {cabins?.prices?.length > 0 && (
                <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="rooms-cabin-prices">
                  {cabins.prices.map((p) => (
                    <div key={p.id}>
                      <dt className="label-caps text-muted-foreground">{tr(p.season)}</dt>
                      {p.rows.map((r) => <dd key={r.nn} className="mt-1.5 text-[0.9375rem] text-foreground tabular">{tr(r)} · {tx("frå", "from")} <span className="font-medium">{nok(r.from, lang)}</span> {tx("per natt", "per night")}</dd>)}
                    </div>
                  ))}
                </dl>
              )}
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
                <Button asChild size="lg" data-testid="rooms-cabins-cta"><Link to="/praktisk?topic=hytte#kontakt">{tx("Spør om ledig hytte", "Ask about cabin availability")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
              </div>
              <p className="mt-5 max-w-[50ch] text-sm leading-relaxed text-muted-foreground">{tx("Send oss datoane og kor mange de blir, så får de pris og informasjon om kva opphaldet omfattar.", "Send us your dates and how many you are, and we will reply with the price and what the stay includes.")}</p>
            </Reveal>
            <Reveal className="order-1 lg:order-2 lg:col-span-7">
              <ParallaxImg src="/images/shore-pier.jpg" alt={tx("Strandlinja ved hotellet med brygge og naust", "The shoreline by the hotel with the pier and boathouses")} className="aspect-[4/3] lg:aspect-[16/11]" position="50% 60%" range={4} sizes={SIZES.half} />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Included + accessibility */}
      <section className="pt-24 lg:pt-32" data-testid="rooms-facilities">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-16 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-6">
              <Kicker className="mb-5">{tx("Inkludert i hotellrommet", "Included in the hotel room")}</Kicker>
              <Headline>{tx("Dette følgjer med", "This comes with the room")}</Headline>
              <Lead>{tx("Frukostbuffé, gratis parkering og trådlaust nett er inkludert når du bur på hotellrom. Alle hotellromma er røykfrie og har eige bad med dusj og toalett.", "Breakfast buffet, free parking and wireless internet are included when you stay in a hotel room. All hotel rooms are non-smoking and have a private bathroom with shower and toilet.")}</Lead>
              <div className="mt-8"><Rows rows={lang === "en" ? INCLUDED.en : INCLUDED.nn} labelWidth="sm:grid-cols-[7rem_1fr]" testid="rooms-included" /></div>
              <div className="mt-6"><TextLink to="/praktisk" testid="rooms-practical-link">{tx("Sjå praktisk informasjon", "See practical information")}</TextLink></div>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-5 lg:col-start-8">
              <div className="border-t border-foreground pt-6" data-testid="rooms-stepfree">
                <Kicker className="mb-4">{tx("Tilrettelegging", "Accessibility")}</Kicker>
                <h3 className="text-[1.5rem] font-medium leading-snug tracking-[-0.015em] text-foreground sm:text-[1.75rem]">{tx("Treng du eit tilrettelagt rom?", "Do you need an accessible room?")}</h3>
                <p className="mt-3 text-[1rem] leading-relaxed text-muted-foreground">{tx("Me har tilrettelagde og allergivenlege rom. Fortel oss kva du treng, så hjelper me deg å velje rom og avklarer tilkomsten før du bestiller.", "We have accessible and allergy-friendly rooms. Tell us what you need and we will help you choose a room and confirm access before you book.")}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild variant="outline" data-testid="rooms-stepfree-contact"><Link to="/praktisk?topic=tilrettelagt#kontakt">{tx("Kontakt oss om rom", "Contact us about rooms")}</Link></Button>
                  <Button asChild variant="ghost" className="tabular" data-testid="rooms-stepfree-call"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {PHONE.display}</a></Button>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Gallery */}
      <section className="pt-24 lg:pt-32" data-testid="rooms-gallery-section">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal><SectionHead kicker={tx("Galleri", "Gallery")} title={tx("Sjå nærare på romma", "A closer look at the rooms")} /></Reveal>
          <Reveal delay={0.05} className="mt-12 lg:mt-16"><HGallery items={gallery} testid="rooms-gallery" /></Reveal>
        </Container>
      </section>

      {/* Help */}
      <section className="py-24 lg:py-32" data-testid="rooms-cta">
        <Container>
          <div className="flex flex-col gap-8 border-t border-border pt-12 lg:flex-row lg:items-end lg:justify-between lg:pt-16">
            <div className="max-w-xl">
              <Headline>{tx("Usikker på kva rom du skal velje?", "Not sure which room to choose?")}</Headline>
              <Lead>{tx("Ring oss på 53 48 80 00, eller spør Fjordverten om skilnadene mellom romtypane.", "Call us on +47 53 48 80 00, or ask Fjordverten about the differences between the room types.")}</Lead>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" variant="outline" className="tabular" data-testid="rooms-call-cta"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring resepsjonen", "Call reception")}</a></Button>
              <Button size="lg" onClick={() => ask(lang === "en" ? "What is the difference between the room types?" : "Kva er skilnaden mellom romtypane?")} data-testid="rooms-ask-cta"><MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
            </div>
          </div>
        </Container>
      </section>

      <ClosingBand
        testid="rooms-closing"
        title={tx("Når vil du kome?", "When would you like to come?")}
        lead={tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}
        image="/images/hotel-mountain.jpg"
        position="50% 55%"
      >
        <Button asChild size="lg" variant="inverse" data-testid="rooms-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="rooms-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
